'use client';
import { useState, useCallback } from 'react';
import { Palette, RefreshCw, Download, Eye, Sparkles } from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import { AgentPageLayout, AgentInputPanel, AgentLabel, AgentStreamOutput, AgentEmptyState } from '@/components/AgentPageLayout';
import { AgentToolbar } from '@/components/AgentToolbar';
import { PROJECT_TYPES, DESIGN_STYLES, AGENT_CONFIGS, downloadAsTextFile } from '@/lib/agent-constants';
import { NasProjectSelector, AgentModeToggle, useNasAnalysis } from '@/components/NasProjectSelector';

interface ColorEntry { name: string; ncs: string; hex: string; area: string; ratio: number; psychEffect?: string; }

export default function ColorAgentPage() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const config = AGENT_CONFIGS.color;
    const [colors, setColors] = useState<ColorEntry[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [aiStream, setAiStream] = useState('');
    const [aiDone, setAiDone] = useState(false);
    const [projectType, setProjectType] = useState('학교');
    const [projectName, setProjectName] = useState('');
    const [style, setStyle] = useState('modern');
    const [mode, setMode] = useState<'manual' | 'nas'>('nas');
    const nasAnalysis = useNasAnalysis();

    const handleNasAnalyze = useCallback(async (projectId: string, selectedFiles: string[]) => {
        setColors([]);
        const fullText = await nasAnalysis.analyze(projectId, selectedFiles, 'color', `스타일: ${style}`);
        if (fullText) {
            try {
                const jsonMatch = fullText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    if (parsed.palette && Array.isArray(parsed.palette)) {
                        setColors(parsed.palette.map((c: any) => ({ name: c.name || '', ncs: c.ncs || '', hex: c.hex || '#888888', area: c.area || '', ratio: c.ratio || 0, psychEffect: c.psychEffect || '' })));
                    }
                }
            } catch { }
        }
    }, [nasAnalysis, style]);

    const analyzeColors = useCallback(async () => {
        setIsProcessing(true);
        setAiStream('');
        setAiDone(false);
        setColors([]);

        try {
            const res = await fetch('/api/color-agent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    projectType,
                    projectName: projectName || `${projectType} 프로젝트`,
                    style,
                    analysisType: 'NCS 색채 분석 + 팔레트 제안 + BF 대비 검증'
                }),
            });

            if (!res.ok) throw new Error('API 오류');
            const reader = res.body?.getReader();
            if (!reader) throw new Error('스트림 없음');
            const decoder = new TextDecoder();
            let fullText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split('\n')) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.substring(6));
                            if (data.type === 'chunk') { fullText += data.text; setAiStream(fullText); }
                            else if (data.type === 'complete') { fullText = data.fullText; setAiStream(fullText); }
                        } catch { /* skip */ }
                    }
                }
            }

            try {
                const jsonMatch = fullText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    if (parsed.palette && Array.isArray(parsed.palette)) {
                        setColors(parsed.palette.map((c: any) => ({
                            name: c.name || '', ncs: c.ncs || '', hex: c.hex || '#888888', area: c.area || '', ratio: c.ratio || 0, psychEffect: c.psychEffect || ''
                        })));
                    }
                }
            } catch { /* raw text */ }
            setAiDone(true);
        } catch (err: any) { setAiStream(`오류: ${err.message}`); setAiDone(true); }
        setIsProcessing(false);
    }, [projectType, projectName, style]);

    const handleExport = () => {
        if (!aiStream) return;
        const filename = `COLOR_분석결과_${projectName || projectType}_${new Date().toISOString().slice(0,10)}.txt`;
        downloadAsTextFile(aiStream, filename);
    };

    return (
        <AgentPageLayout config={config} icon={<Palette className="w-5 h-5" />} isProcessing={isProcessing} aiDone={aiDone}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-3">
                    <AgentInputPanel>
                        <AgentModeToggle mode={mode} onModeChange={setMode} accentBg={config.accentBg} accentText={config.accentText} accentBorder={config.accentBorder} />
                        {mode === 'nas' ? (
                            <NasProjectSelector agentType="color" accentBg={config.accentBg} accentText={config.accentText} accentBorder={config.accentBorder} gradient={config.gradient} onAnalyze={handleNasAnalyze} isProcessing={nasAnalysis.isProcessing}>
                                <div>
                                    <AgentLabel>디자인 스타일</AgentLabel>
                                    <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                                        {DESIGN_STYLES.map(s => (
                                            <button key={s.value} onClick={() => setStyle(s.value)}
                                                className={`py-1.5 rounded-lg text-[10px] font-bold cursor-pointer capitalize transition ${style === s.value ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}` : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>{s.label}</button>
                                        ))}
                                    </div>
                                </div>
                            </NasProjectSelector>
                        ) : (
                            <>
                                <div>
                                    <AgentLabel>건물 용도</AgentLabel>
                                    <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                                        {PROJECT_TYPES.map(t => (
                                            <button key={t} onClick={() => setProjectType(t)}
                                                className={`py-2 rounded-lg text-[10px] font-bold cursor-pointer transition ${projectType === t ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}` : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>{t}</button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <AgentLabel>프로젝트명</AgentLabel>
                                    <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="예: 대곡고등학교"
                                        className={`w-full mt-1.5 px-3 py-2.5 rounded-lg text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'}`} />
                                </div>
                                <div>
                                    <AgentLabel>디자인 스타일</AgentLabel>
                                    <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                                        {DESIGN_STYLES.map(s => (
                                            <button key={s.value} onClick={() => setStyle(s.value)}
                                                className={`py-2 rounded-lg text-[10px] font-bold cursor-pointer capitalize transition ${style === s.value ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}` : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>{s.label}</button>
                                        ))}
                                    </div>
                                </div>
                                <button onClick={analyzeColors} disabled={isProcessing}
                                    className={`w-full py-3 rounded-xl bg-gradient-to-r ${config.gradient} text-white text-xs font-bold cursor-pointer disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg`}>
                                    {isProcessing ? <><RefreshCw className="w-4 h-4 animate-spin" />AI 분석 중...</> : <><Sparkles className="w-4 h-4" />AI 색채 분석 시작</>}
                                </button>
                            </>
                        )}
                    </AgentInputPanel>
                </div>
                <div className="lg:col-span-9 space-y-4">
                    {colors.length > 0 && (
                        <>
                            {/* Palette Visual */}
                            <div className={`rounded-2xl border p-4 ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                <h4 className="text-xs font-bold mb-3 flex items-center gap-2"><Eye className="w-4 h-4 text-pink-400" /> 프로젝트 색채 팔레트 (AI 생성)</h4>
                                <div className="flex rounded-xl overflow-hidden h-16">{colors.map((c, i) => (
                                    <div key={i} style={{ backgroundColor: c.hex, width: `${Math.max(c.ratio, 5)}%` }} className="relative group cursor-pointer">
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/40">
                                            <span className="text-white text-[10px] font-bold">{c.ncs}</span>
                                        </div>
                                    </div>
                                ))}</div>
                            </div>
                            {/* Color Table */}
                            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                <div className={`px-4 py-2.5 border-b flex items-center justify-between ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                    <span className="text-xs font-bold">NCS 색채 분석표 ({colors.length}색)</span>
                                    <button onClick={handleExport} className={`text-[10px] px-3 py-1.5 rounded-lg cursor-pointer flex items-center gap-1 font-bold transition hover:opacity-80 ${isDark ? 'bg-pink-500/20 text-pink-300' : 'bg-pink-50 text-pink-600'}`}>
                                        <Download className="w-3 h-3" />내보내기
                                    </button>
                                </div>
                                <table className="w-full text-[10px]">
                                    <thead><tr className={isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}>
                                        {['색상', '명칭', 'NCS 코드', 'HEX', '적용 부위', '비율', '심리 효과'].map(h => <th key={h} className={`px-3 py-2.5 font-bold text-left ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>)}
                                    </tr></thead>
                                    <tbody>{colors.map((c, i) => (
                                        <tr key={i} className={`border-t ${isDark ? 'border-white/5 hover:bg-white/[0.03]' : 'border-gray-100 hover:bg-gray-50'} transition`}>
                                            <td className="px-3 py-2.5"><div className="w-6 h-6 rounded-lg border" style={{ backgroundColor: c.hex, borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb' }} /></td>
                                            <td className="px-3 py-2.5 font-semibold">{c.name}</td>
                                            <td className="px-3 py-2.5 font-mono text-pink-400">{c.ncs}</td>
                                            <td className="px-3 py-2.5 font-mono">{c.hex}</td>
                                            <td className={`px-3 py-2.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{c.area}</td>
                                            <td className="px-3 py-2.5"><div className="flex items-center gap-1"><div className={`w-12 h-2 rounded-sm overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}><div className="h-full rounded-sm" style={{ width: `${c.ratio}%`, backgroundColor: c.hex }} /></div><span className="font-mono">{c.ratio}%</span></div></td>
                                            <td className={`px-3 py-2.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{c.psychEffect || '-'}</td>
                                        </tr>
                                    ))}</tbody>
                                </table>
                            </div>
                        </>
                    )}

                    <AgentStreamOutput aiStream={aiStream} isProcessing={isProcessing} accentColor="text-pink-400" />
                    {aiStream && !isProcessing && (
                        <AgentToolbar title="컬러 분석" content={aiStream} agentType="color" />
                    )}

                    {!aiStream && !isProcessing && colors.length === 0 && (
                        <AgentEmptyState
                            icon={<Palette className={`w-8 h-8 ${isDark ? 'text-pink-500/30' : 'text-pink-200'}`} />}
                            message="프로젝트 정보를 입력하고 AI 색채 분석을 시작하세요"
                        />
                    )}
                </div>
            </div>
        </AgentPageLayout>
    );
}
